import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Drawer from '@mui/material/Drawer';
import ToggleButton from '@mui/material/ToggleButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import { IoClose as Close } from 'react-icons/io5';
import { BsFileEarmarkSpreadsheet as ExcelIcon } from 'react-icons/bs';
import { BsFiletypeCsv as CsvIcon } from 'react-icons/bs';
import { BsClockHistory as HistoryIcon } from 'react-icons/bs';
import { BsGearFill as SettingsIcon } from 'react-icons/bs';
import { BsInfoCircle as InfoIcon } from 'react-icons/bs';
import { BsArrowUp as ArrowUpIcon } from 'react-icons/bs';
import { BsArrowDown as ArrowDownIcon } from 'react-icons/bs';
import { BsGripVertical as DragIcon } from 'react-icons/bs';
import { BsBraces as VariableIcon } from 'react-icons/bs';
import { ReactComponent as DownloadIcon } from '@Assets/download.svg';
import UserPreferenceService from '@Helpers/URLHelpers/UserPreferenceService';
import ManagementService from '@Helpers/URLHelpers/ManagementService';
import TicketExportJobService from '@Helpers/URLHelpers/TicketExportJobService';
import FieldService from '@Helpers/URLHelpers/FieldService';
import FormUserAccessLinkService from '@Helpers/URLHelpers/KaiziqResolve/views/FormUserAccessLinkService';
import TicketUserEmailCountViewService from '@Helpers/URLHelpers/KaiziqResolve/views/TicketUserEmailCountViewService';
import TicketExportJobRow from '@IssueConsoleRepresentationalDisplay/TicketExportJobRow';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ErrorHandler from '@Root/ErrorHandler';
import './ConfirmDownloadForm.scss';

const DATE_FORMAT = 'YYYY-MM-DD';

const DATE_RANGE_PRESETS = [
    { key: 'thisWeek', label: 'This week', range: () => ({ from: dayjs().startOf('week'), to: dayjs() }) },
    { key: 'lastWeek', label: 'Last week', range: () => ({ from: dayjs().subtract(1, 'week').startOf('week'), to: dayjs().subtract(1, 'week').endOf('week') }) },
    { key: 'thisMonth', label: 'This month', range: () => ({ from: dayjs().startOf('month'), to: dayjs() }) },
    { key: 'lastMonth', label: 'Last month', range: () => ({ from: dayjs().subtract(1, 'month').startOf('month'), to: dayjs().subtract(1, 'month').endOf('month') }) },
    { key: 'last7', label: 'Last 7 days', range: () => ({ from: dayjs().subtract(7, 'day'), to: dayjs() }) },
    { key: 'last30', label: 'Last 30 days', range: () => ({ from: dayjs().subtract(30, 'day'), to: dayjs() }) },
    { key: 'last6m', label: 'Last 6 months', range: () => ({ from: dayjs().subtract(6, 'month'), to: dayjs() }) },
    { key: 'lifetime', label: 'Lifetime' },
    { key: 'custom', label: 'Custom' }
];

const getPresetRange = (presetKey) => {
    const preset = DATE_RANGE_PRESETS.find((item) => item.key === presetKey);
    if (!preset || !preset.range) {
        return { from: '', to: '' };
    }
    const { from, to } = preset.range();
    return { from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) };
};

const detectDateRangePreset = (from, to) => {
    if (!from && !to) {
        return 'lifetime';
    }
    const match = DATE_RANGE_PRESETS.find((preset) => {
        if (!preset.range) {
            return false;
        }
        const range = getPresetRange(preset.key);
        return range.from === from && range.to === to;
    });
    return match ? match.key : 'custom';
};

export default function ConfirmDownloadForm(props) {
    const [cursor, setCursor] = useState('auto');
    const [splitBy, setSplitBy] = useState('');
    const [refresh, setRefresh] = useState(false);
    const [, setDefaultLoadError] = useState(false);
    const [isCheckedSelectAll, setIsCheckedSelectAll] = useState(false);
    const { activeUserPreferences, activeUserPreferencesAll, setActiveUserPreferencesAll} = useReduxActiveUser();
    const { currentProjectId, getSimpleUsersByProjectId } = useReduxProject();
    const userPreferenceService = new UserPreferenceService();
    const [exportToExcel, setExportToExcel] = useState(true);
    const [ticketExportCount, setTicketExportCount] = useState(0);
    const [isExporting, setIsExporting] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [notifyDownload, setNotifyDownload] = useState(false);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [ticketExportJobList, setTicketExportJobList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(false);
    const [variableMenuAnchor, setVariableMenuAnchor] = useState(null);
    const [currentCustomFieldIndex, setCurrentCustomFieldIndex] = useState(null);
    const [statusOptions, setStatusOptions] = useState([]);
    const [assigneeOptions, setAssigneeOptions] = useState([]);
    const [createdByEmailOptions, setCreatedByEmailOptions] = useState([]);
    const [dateRangePreset, setDateRangePreset] = useState('lifetime');

    const toDateInputValue = (timestamp) => {
        if (!timestamp || Number(timestamp) === 0) {
            return '';
        }
        const date = new Date(Number(timestamp));
        if (Number.isNaN(date.getTime())) {
            return '';
        }
        return date.toISOString().slice(0, 10);
    };

    const toDayStartTimestamp = (dateString) => {
        if (!dateString) {
            return 0;
        }
        const date = new Date(`${dateString}T00:00:00`);
        return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    };

    const toDayEndTimestamp = (dateString) => {
        if (!dateString) {
            return 0;
        }
        const date = new Date(`${dateString}T23:59:59.999`);
        return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    };

    const [exportFilters, setExportFilters] = useState({
        searchQuery: props?.filters?.ticketFilter?.searchQuery || '',
        statusList: props?.filters?.ticketFilter?.statusList || props?.statusList || [],
        kaiziqResolveConnectionIdList: props?.filters?.ticketFilter?.kaiziqResolveConnectionIdList || (props?.kaiziqResolveConnectionId ? [props.kaiziqResolveConnectionId] : []),
        assigneeIdList: props?.filters?.ticketFilter?.assigneeIdList || [],
        createdByEmailList: props?.filters?.ticketFilter?.createdByEmailList || [],
        createTimeStampFrom: toDateInputValue(props?.filters?.ticketFilter?.createTimeStampFrom),
        createTimeStampTo: toDateInputValue(props?.filters?.ticketFilter?.createTimeStampTo),
        sortBy: props?.filters?.ticketFilter?.sortBy || 'createTimeStamp',
        sortAsc: props?.filters?.ticketFilter?.sortAsc ?? false
    });

    const [selectedOptions, setSelectedOptions] = useState([
        {
            id: 0,
            field: 'ticketId',
            description: 'Ticket Tag',
            display: 'Tag',
            customField: false
        },
        {
            id: 1,
            field: 'ticketTitle',
            description: 'Title of the Ticket',
            display: 'Title',
            customField: false
        },
        {
            id: 2,
            field: 'status',
            description: 'Status of the Ticket',
            display: 'Status',
            customField: false
        },
        {
            id: 3,
            field: 'reportedAt',
            description: 'Reported Date',
            display: 'Reported On',
            customField: false
        },
        {
            id: 4,
            field: 'createdByFullName',
            description: 'Reporter Name',
            display: 'Reported By',
            customField: false
        },
        {
            id: 5,
            field: 'reopenTimeStamp',
            description: 'Reopened Date',
            display: 'Reopened On',
            customField: false
        },
        {
            id: 6,
            field: 'closeTimeStamp',
            description: 'Closed Date',
            display: 'Closed On',
            customField: false
        },
        {
            id: 7,
            field: 'assigneeUserName',
            description: 'Assignee Username',
            display: 'Assignee',
            customField: false
        }
    ]);

    const [downloadOptions, setDownloadOptions] = useState([
        {
            id: 0,
            field: 'ticketId',
            description: 'Ticket Tag',
            display: 'Tag',
            customField: false
        },
        {
            id: 1,
            field: 'ticketTitle',
            description: 'Title of the Ticket',
            display: 'Title',
            customField: false
        },
        {
            id: 2,
            field: 'status',
            description: 'Status of the Ticket',
            display: 'Status',
            customField: false
        },
        {
            id: 3,
            field: 'reportedAt',
            description: 'Reported Date',
            display: 'Reported On',
            customField: false
        },
        {
            id: 4,
            field: 'createdByFullName',
            description: 'Reporter Name',
            display: 'Reported By',
            customField: false
        },
        {
            id: 5,
            field: 'reopenTimeStamp',
            description: 'Reopened Date',
            display: 'Reopened On',
            customField: false
        },
        {
            id: 6,
            field: 'closeTimeStamp',
            description: 'Closed Date',
            display: 'Closed On',
            customField: false
        },
        {
            id: 7,
            field: 'assigneeUserName',
            description: 'Assignee Username',
            display: 'Assignee',
            customField: false
        },
        {
            id: 8,
            field: 'createdByEmail',
            description: 'Reporter Email',
            display: 'Reporter Email',
            customField: false
        },
        {
            id: 9,
            field: 'createdByContactNumber',
            description: 'Reporter Contact Number',
            display: 'Reporter Contact Number',
            customField: false
        },
        {
            id: 10,
            field: 'priority',
            description: 'Priority',
            display: 'Priority',
            customField: false
        },
        {
            id: 11,
            field: 'labels',
            description: 'Labels added in the Ticket',
            display: 'Labels',
            customField: false
        },
        {
            id: 12,
            field: 'comments',
            description: 'Comments added in the Ticket',
            display: 'Comments',
            customField: false
        },
        {
            id: 13,
            field: "field_21",
            description: "customField",
            display: "2",
            customField: true
        },
        {
            id: 14,
            field: "field_22",
            description: "customField",
            display: "= ({field_212} + 10)",
            customField: true
        },
        {
            id: 15,
            field: "field_23",
            description: "customField",
            display: "Title",
            customField: true
        }
    ]);

    const ticketExportJobService = new TicketExportJobService();
    const managementService = new ManagementService();
    const fieldService = new FieldService();
    const formUserAccessLinkService = new FormUserAccessLinkService();
    const ticketUserEmailCountViewService = new TicketUserEmailCountViewService();
    const errorHandler = new ErrorHandler();
    const className = 'confirmDownloadForm';

    const customFields = downloadOptions.filter(option => option.customField);
    const standardFields = downloadOptions.filter(option => !option.customField);

    // Available variables for custom fields
    const getAvailableVariables = () => {
        const standardVariables = [
            { name: 'ticketId', description: 'Ticket ID/Tag', type: 'standard' },
            { name: 'ticketTitle', description: 'Ticket Title', type: 'standard' },
            { name: 'status', description: 'Ticket Status', type: 'standard' },
            { name: 'reportedAt', description: 'Reported Date', type: 'standard' },
            { name: 'createdByFullName', description: 'Reporter Full Name', type: 'standard' },
            { name: 'createdByEmail', description: 'Reporter Email', type: 'standard' },
            { name: 'createdByContactNumber', description: 'Reporter Contact Number', type: 'standard' },
            { name: 'assigneeUserName', description: 'Assignee Username', type: 'standard' },
            { name: 'priority', description: 'Priority', type: 'standard' },
            { name: 'labels', description: 'Labels', type: 'standard' },
            { name: 'comments', description: 'Comments', type: 'standard' }
        ];

        const customFieldVariables = customFields
            .filter((field) => {
                if (currentCustomFieldIndex !== null) {
                    const currentField = downloadOptions[currentCustomFieldIndex];
                    return field.id !== currentField.id;
                }
                return true;
            })
            .map(field => ({
                name: field.field,
                description: field.display || field.field,
                type: 'custom'
            }));

        return [...standardVariables, ...customFieldVariables];
    };

    let interval;

    useEffect(() => {
        try {
            if(!!activeUserPreferences?.defaults?.ticketDownloadOptions && !!activeUserPreferences?.defaults?.ticketDownloadOptions[props?.kaiziqResolveConnectionId]) {
                let downloadOptionsDefaultList = activeUserPreferences.defaults.ticketDownloadOptions[props?.kaiziqResolveConnectionId].downloadOptions;
                let filterCustomField = downloadOptionsDefaultList.filter((item)=> item.customField);
                let filterIdList = new Set(filterCustomField.map((item)=> item.id));
                filterCustomField = Array.from(filterIdList).map((id)=> filterCustomField.find((item)=> item.id === id));
                setDownloadOptions([...downloadOptions, ...filterCustomField]);
                setSelectedOptions(activeUserPreferences.defaults.ticketDownloadOptions[props?.kaiziqResolveConnectionId].selectedOptions);
                setSplitBy(activeUserPreferences.defaults.ticketDownloadOptions[props?.kaiziqResolveConnectionId].splitBy);
            }
        } catch (err) {
            errorHandler.logError(err);
            setDefaultLoadError(true);
        }
    }, [])

    useEffect(()=>{
        renderData();
    }, [])

    useEffect(() => {
        const createTimeStampFrom = toDateInputValue(props?.filters?.ticketFilter?.createTimeStampFrom);
        const createTimeStampTo = toDateInputValue(props?.filters?.ticketFilter?.createTimeStampTo);
        setExportFilters({
            searchQuery: props?.filters?.ticketFilter?.searchQuery || '',
            statusList: props?.filters?.ticketFilter?.statusList || props?.statusList || [],
            kaiziqResolveConnectionIdList: props?.filters?.ticketFilter?.kaiziqResolveConnectionIdList || (props?.kaiziqResolveConnectionId ? [props.kaiziqResolveConnectionId] : []),
            assigneeIdList: props?.filters?.ticketFilter?.assigneeIdList || [],
            createdByEmailList: props?.filters?.ticketFilter?.createdByEmailList || [],
            createTimeStampFrom,
            createTimeStampTo,
            sortBy: props?.filters?.ticketFilter?.sortBy || 'createTimeStamp',
            sortAsc: props?.filters?.ticketFilter?.sortAsc ?? false
        });
        setDateRangePreset(detectDateRangePreset(createTimeStampFrom, createTimeStampTo));
    }, [props?.filters, props?.statusList, props?.kaiziqResolveConnectionId]);

    useEffect(() => {
        loadFilterOptions();
    }, [currentProjectId, props?.kaiziqResolveConnectionId]);

    useEffect(()=>{
        interval = setInterval(() => {
             fetchRunningInstanceCount();
        }, 3000);
        return () => {
            clearInterval(interval);
        }
    },[ticketExportCount, isExporting])

    const fetchRunningInstanceCount = async()=> {
        try {
            if(ticketExportCount !== 0) {
                let response = await ticketExportJobService.getRunningInstanceCount(currentProjectId, props.kaiziqResolveConnectionId);
                if(response.status < 400) {
                    let count = parseInt(response.body);
                    setTicketExportCount(count);
                }
            } else {
                if(isExporting) {
                    setIsExporting(false);
                    setNotifyDownload(true);
                    const failed = await didLatestExportFail();
                    if(failed) {
                        setSnackBarMessage(failed);
                        setSnackBarType('error');
                    } else {
                        setSnackBarMessage('Tickets List Exported...');
                        setSnackBarType('success');
                    }
                    setSnackBarOpen(true);
                }
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const didLatestExportFail = async () => {
        try {
            const response = await ticketExportJobService.getListByProjectIdAndConnectionId(currentProjectId, props.kaiziqResolveConnectionId);
            if(response.status >= 400) {
                return '';
            }
            const jobs = JSON.parse(response.body);
            const latest = Array.isArray(jobs) ? jobs[0] : null;
            if(latest?.conclusion !== 'failed') {
                return '';
            }
            let reason = '';
            try {
                reason = latest?.details ? (JSON.parse(latest.details)?.error || '') : '';
            } catch (parseErr) {
                reason = '';
            }
            return reason ? `Ticket export failed: ${reason}` : 'Ticket export failed. Please try again.';
        } catch (err) {
            return '';
        }
    };

    const renderData = async ()=> {
        try {
            if(!props.filters || !props.filters.filter || !props.filters.filter.recurringTaskParentId) {
                setExportToExcel(true);
                return;
            }
            let count = await managementService.countIssueListFilteredForExports(props.filters);
            count = JSON.parse(count.body);
            if(count <= 1000) {
                setExportToExcel(true);
            }else {
                setExportToExcel(false);
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const loadFilterOptions = async () => {
        if (!currentProjectId || !props?.kaiziqResolveConnectionId) {
            return;
        }
        setIsFilterOptionsLoading(true);
        try {
            const userPromise = getSimpleUsersByProjectId(currentProjectId);
            const statusFilter = {
                fieldFilter: {
                    projectIdList: [currentProjectId],
                    tableType: 'kaiziqResolveConnection',
                    tableId: props.kaiziqResolveConnectionId,
                    filterAttribute1List: ["admin"]
                }
            };
            const statusPromise = fieldService.getListFiltered(statusFilter);

            const [allUsers, adminFieldListObj] = await Promise.all([userPromise, statusPromise]);

            const normalizedUsers = (allUsers || []).map((user) => ({
                id: Number(user.id),
                label: user.fullName || user.userName,
                userName: user.userName
            })).filter((user) => !!user.id);
            setAssigneeOptions(normalizedUsers);

            const parsedFieldResponse = JSON.parse(adminFieldListObj?.body || '{}');
            const adminFieldList = parsedFieldResponse?.result || [];
            const statusField = adminFieldList.find((field) => field.title === 'status');
            const parsedStatusOptions = statusField?.options ? JSON.parse(statusField.options) : [];
            const normalizedStatusOptions = (parsedStatusOptions || []).map((status) => ({
                id: Number(status.id),
                value: status.value,
                label: (status.value || '').charAt(0).toUpperCase() + (status.value || '').slice(1)
            })).filter((status) => !!status.id);
            setStatusOptions(normalizedStatusOptions);

            const emailSet = new Set();
            try {
                let formUserListObj = await formUserAccessLinkService.getListByForm(props.kaiziqResolveConnectionId);
                formUserListObj = JSON.parse(formUserListObj?.body || '[]');
                formUserListObj.forEach((row) => {
                    if (!!row?.createdByEmail) {
                        emailSet.add(row.createdByEmail);
                    }
                });
            } catch (err) {
                errorHandler.logError(err);
            }

            try {
                let ticketUserEmailCountObj = await ticketUserEmailCountViewService.getListByForm(props.kaiziqResolveConnectionId);
                ticketUserEmailCountObj = JSON.parse(ticketUserEmailCountObj?.body || '[]');
                ticketUserEmailCountObj.forEach((row) => {
                    if (!!row?.createdByEmail) {
                        emailSet.add(row.createdByEmail);
                    }
                });
            } catch (err) {
                errorHandler.logError(err);
            }

            setCreatedByEmailOptions(Array.from(emailSet).sort((a, b) => a.localeCompare(b)));
        } catch (err) {
            errorHandler.handleError(err);
        }
        setIsFilterOptionsLoading(false);
    };

    const resetExportFilters = () => {
        setExportFilters({
            searchQuery: '',
            statusList: [],
            kaiziqResolveConnectionIdList: props?.kaiziqResolveConnectionId ? [props.kaiziqResolveConnectionId] : [],
            assigneeIdList: [],
            createdByEmailList: [],
            createTimeStampFrom: '',
            createTimeStampTo: '',
            sortBy: 'createTimeStamp',
            sortAsc: false
        });
        setDateRangePreset('lifetime');
    };

    const handleSubmit = async (e)=> {
        try {
            e?.preventDefault();
            setCursor('wait');
            let allOptions = [];
            const filters = props.filters;
            const ticketFilterPayload = {
                ...(filters?.ticketFilter || {}),
                searchQuery: exportFilters.searchQuery?.trim() || '',
                statusList: exportFilters.statusList || [],
                kaiziqResolveConnectionIdList: exportFilters.kaiziqResolveConnectionIdList?.length > 0 ? exportFilters.kaiziqResolveConnectionIdList : [props.kaiziqResolveConnectionId],
                assigneeIdList: exportFilters.assigneeIdList || [],
                createdByEmailList: exportFilters.createdByEmailList || [],
                createTimeStampFrom: toDayStartTimestamp(exportFilters.createTimeStampFrom),
                createTimeStampTo: toDayEndTimestamp(exportFilters.createTimeStampTo),
                sortBy: exportFilters.sortBy || 'createTimeStamp',
                sortAsc: !!exportFilters.sortAsc
            };
            let sortAllOption = downloadOptions.sort((a, b)=> a.id - b.id);
            let sortSelectedOption = selectedOptions.sort((a, b)=> a.id - b.id);
            if(!isCheckedSelectAll) {
                allOptions = sortSelectedOption;
            }
            else{
                allOptions = sortAllOption
            }
            let defaultDownloadOptionsObj = {
                projectId: currentProjectId,
                downloadOptions: allOptions,
                splitBy: splitBy
            }
            let ticketExportBody = {
                page: filters.page,
                size: filters.size,
                projectId: currentProjectId,
                kaiziqResolveConnectionId: props.kaiziqResolveConnectionId,
                ticketFilter: ticketFilterPayload,
                downloadOptions: JSON.stringify(defaultDownloadOptionsObj)
            }
            let response = await ticketExportJobService.createTicketExportJob(ticketExportBody);
            if(response.status < 400) {
                setSnackBarMessage('Ticket List downloading in progress...');
                setSnackBarType('success');
                setSnackBarOpen(true);
                setIsExporting(true);
                setTicketExportCount(1);
            } else {
                setSnackBarMessage(response.message);
                setSnackBarType('error');
                setSnackBarOpen(true);
                setIsExporting(false);
                setTicketExportCount(0);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const updateSetDefaultDownloadOptionsPreference = async(e) => {
        try {
            e?.preventDefault();
            setCursor('wait');
            let preferences = activeUserPreferences;
            if(preferences?.defaults === undefined || preferences?.defaults === null) {
                preferences.defaults = {};
            }
            let defaultDownloadOptionsObj = {
                projectId: currentProjectId,
                kaiziqResolveConnectionId: props?.kaiziqResolveConnectionId,
                downloadOptions: downloadOptions,
                selectedOptions: selectedOptions,
                splitBy: splitBy
            }
            if(preferences?.defaults?.ticketDownloadOptions === undefined || preferences?.defaults?.ticketDownloadOptions === null) {
                preferences.defaults.ticketDownloadOptions = {};
            }
            preferences.defaults.ticketDownloadOptions[props?.kaiziqResolveConnectionId] = defaultDownloadOptionsObj;
            let tempActiveUserPreferences = activeUserPreferencesAll;
            tempActiveUserPreferences.preferences = JSON.stringify(preferences);
            userPreferenceService.updateUserPreference(tempActiveUserPreferences);
            setActiveUserPreferencesAll(tempActiveUserPreferences);
            await handleSubmit();
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleOptionDisplayChange = (e, index) => {
        let sanitizedDisplay = DOMPurify.sanitize(e.target.value);
        let downloadOptionsObj = [...downloadOptions];
        let option = downloadOptionsObj[index];
        option.display = sanitizedDisplay;
        downloadOptionsObj[index] = option;
        setDownloadOptions(downloadOptionsObj);
        index = selectedOptions.findIndex((item) => item.id === option.id)
        if(index > -1) {
            let selectedOptionsObj = [...selectedOptions];
            option = selectedOptionsObj[index];
            option.display = sanitizedDisplay;
            selectedOptionsObj[index] = option;
            setSelectedOptions(selectedOptionsObj);
        }
        setRefresh(!refresh);
    }

    const handleSelectAllField = (e)=> {
        if(!isCheckedSelectAll) {
            setSelectedOptions([...downloadOptions]);
        } else{
            setSelectedOptions([]);
        }
        setIsCheckedSelectAll(!isCheckedSelectAll);
    }

    const handleFieldSelect = (e, option)=> {
        setIsCheckedSelectAll(false);
        if(e.target.checked) {
            setSelectedOptions([...selectedOptions, option]);
            if(selectedOptions.length === downloadOptions.length - 1) {
                setIsCheckedSelectAll(true);
            }
        } else {
            setSelectedOptions(selectedOptions.filter((item) => item.id !== option.id));
        }
    }

    const handleSplitBy = (e)=> {
        let value = e.target.value;
        setSplitBy(value);
    }

    const handleFilterChange = (key, value) => {
        setExportFilters((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleMultiFilterChange = (key, value) => {
        setExportFilters((prev) => ({
            ...prev,
            [key]: Array.isArray(value) ? value : []
        }));
    };

    const handleDateRangePresetChange = (e, value) => {
        if (!value) {
            return;
        }
        setDateRangePreset(value);
        if (value === 'custom') {
            return;
        }
        const { from, to } = getPresetRange(value);
        setExportFilters((prev) => ({
            ...prev,
            createTimeStampFrom: from,
            createTimeStampTo: to
        }));
    };

    const handleDatePickerChange = (key, value) => {
        const formatted = value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
        setDateRangePreset('custom');
        handleFilterChange(key, formatted);
    };

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    const renderTicketExportJobList = async()=> {
        try {
            setIsLoading(true);
            let response = await ticketExportJobService.getListByProjectIdAndConnectionId(currentProjectId, props?.kaiziqResolveConnectionId);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                setTicketExportJobList(response);
            }
        }catch(err) {
            errorHandler.handleError(err);
        }
        setIsLoading(false);
    }

    const handleHistoryButtonClick = async ()=> {
        setNotifyDownload(false);
        renderTicketExportJobList();
        handleDrawer();
    }

    const handleDrawer = ()=>{
        setOpenDrawer(!openDrawer);
    }

    const moveSelectedField = (fromIndex, direction) => {
        const newSelectedOptions = [...selectedOptions];
        const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
        
        if (toIndex >= 0 && toIndex < newSelectedOptions.length) {
            [newSelectedOptions[fromIndex], newSelectedOptions[toIndex]] = 
            [newSelectedOptions[toIndex], newSelectedOptions[fromIndex]];
            
            setSelectedOptions(newSelectedOptions);
        }
    };

    const handleVariableMenuClose = () => {
        setVariableMenuAnchor(null);
        setCurrentCustomFieldIndex(null);
    };

    const insertVariable = (variableName) => {
        if (currentCustomFieldIndex !== null) {
            const downloadOptionsObj = [...downloadOptions];
            const option = downloadOptionsObj[currentCustomFieldIndex];
            const currentValue = option.display || '';
            const cursorPosition = currentValue.length;
            const newValue = currentValue.slice(0, cursorPosition) + `{${variableName}}` + currentValue.slice(cursorPosition);
            
            option.display = newValue;
            downloadOptionsObj[currentCustomFieldIndex] = option;
            setDownloadOptions(downloadOptionsObj);
            
            const selectedIndex = selectedOptions.findIndex((item) => item.id === option.id);
            if (selectedIndex > -1) {
                const selectedOptionsObj = [...selectedOptions];
                selectedOptionsObj[selectedIndex].display = newValue;
                setSelectedOptions(selectedOptionsObj);
            }
            
            setRefresh(!refresh);
        }
        handleVariableMenuClose();
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.closePopup}
            maxWidth="xl"
            fullWidth
            className={className}
            PaperProps={{
                className: className + '__paper'
            }}
        >
            <DialogTitle className={className + '__header'}>
                <Box className={className + '__header__content'}>
                    <Box className={className + '__header__icon'}>
                        <DownloadIcon />
                    </Box>
                    <Box className={className + '__header__text'}>
                        <Typography className={className + '__header__title'}>
                            Configure Download
                        </Typography>
                        <Typography className={className + '__header__subtitle'}>
                            Select fields, set order, and format for your export
                        </Typography>
                    </Box>
                </Box>
                <Box className={className + '__header__actions'}>
                    <Tooltip title="Export History">
                        <IconButton
                            onClick={handleHistoryButtonClick}
                            className={className + '__header__actions__history'}
                            size="small"
                        >
                            <Badge 
                                variant="dot" 
                                color="error" 
                                invisible={!notifyDownload}
                                className={className + '__header__actions__history__badge'}
                            >
                                <HistoryIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                    {isExporting && (
                        <CircularProgress size={20} className={className + '__header__actions__progress'} />
                    )}
                    <IconButton
                        onClick={props.closePopup}
                        disabled={cursor === 'wait'}
                        className={className + '__header__close'}
                        size="small"
                    >
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent className={className + '__content'}>
                {/* Export Format & Split Options */}
                <Paper className={className + '__config'}>
                    <Box className={className + '__config__header'}>
                        <SettingsIcon className={className + '__config__header__icon'} />
                        <Typography className={className + '__config__header__title'}>
                            Export Configuration
                        </Typography>
                    </Box>
                    
                    <Box className={className + '__config__options'}>
                        <Box className={className + '__config__options__format'}>
                            <Typography className={className + '__config__options__format__label'}>
                                Export Format
                            </Typography>
                            <Box className={className + '__config__options__format__buttons'}>
                                <Chip
                                    icon={<ExcelIcon />}
                                    label={exportToExcel ? 'Excel (.xlsx)' : 'Excel Format'}
                                    color={exportToExcel ? 'primary' : 'default'}
                                    variant={exportToExcel ? 'filled' : 'outlined'}
                                    className={className + '__config__options__format__chip'}
                                />
                                <Chip
                                    icon={<CsvIcon />}
                                    label={!exportToExcel ? 'CSV (.csv)' : 'CSV Format'}
                                    color={!exportToExcel ? 'primary' : 'default'}
                                    variant={!exportToExcel ? 'filled' : 'outlined'}
                                    className={className + '__config__options__format__chip'}
                                />
                            </Box>
                        </Box>

                        <Box className={className + '__config__options__split'}>
                            <FormControl size="small" className={className + '__config__options__split__control'}>
                                <InputLabel id="split-sheets-by-label">Split Sheets By</InputLabel>
                                <Select
                                    labelId="split-sheets-by-label"
                                    value={splitBy}
                                    size="small"
                                    label="Split Sheets By"
                                    onChange={handleSplitBy}
                                    className={className + '__config__options__split__select'}
                                >
                                    <MenuItem value={''}>N/A</MenuItem>
                                    <MenuItem value={'assigneeUserName'}>Assignee</MenuItem>
                                    <MenuItem value={'status'}>Status</MenuItem>
                                    <MenuItem value={'priority'}>Priority</MenuItem>
                                    <MenuItem value={'createdByFullName'}>Reporter</MenuItem>
                                </Select>
                                <FormHelperText>
                                    Split data into separate sheets based on selected property
                                </FormHelperText>
                            </FormControl>
                        </Box>
                    </Box>
                </Paper>

                <Paper className={className + '__filters'}>
                    <Box className={className + '__filters__header'}>
                        <Box>
                            <Typography className={className + '__filters__header__title'}>
                                Export Filters
                            </Typography>
                            <Typography className={className + '__filters__header__subtitle'}>
                                Narrow down exported tickets by search, status, user, date, and sort.
                            </Typography>
                        </Box>
                        <Button
                            variant='text'
                            size='small'
                            onClick={resetExportFilters}
                            className={className + '__filters__header__reset'}>
                            Reset
                        </Button>
                    </Box>

                    {isFilterOptionsLoading && (
                        <Box className={className + '__filters__loading'}>
                            <CircularProgress size={18} />
                            <Typography className={className + '__filters__loading__text'}>
                                Loading filter options...
                            </Typography>
                        </Box>
                    )}

                    <Box className={className + '__filters__grid'}>
                        <TextField
                            size='small'
                            label='Search Query'
                            value={exportFilters.searchQuery}
                            onChange={(e) => handleFilterChange('searchQuery', DOMPurify.sanitize(e.target.value))}
                            className={className + '__filters__grid__item'}
                        />

                        <FormControl size='small' className={className + '__filters__grid__item'}>
                            <InputLabel id='export-filter-status-list-label'>Status</InputLabel>
                            <Select
                                labelId='export-filter-status-list-label'
                                multiple
                                value={exportFilters.statusList}
                                label='Status'
                                onChange={(e) => handleMultiFilterChange('statusList', e.target.value)}
                                renderValue={(selected) => {
                                    const selectedStatuses = statusOptions.filter((status) => selected.includes(status.id));
                                    return selectedStatuses.length > 0 ? selectedStatuses.map((status) => status.label).join(', ') : 'All';
                                }}>
                                {statusOptions.map((status) => (
                                    <MenuItem key={status.id} value={status.id}>
                                        <Checkbox checked={exportFilters.statusList.includes(status.id)} size='small' />
                                        <ListItemText primary={status.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size='small' className={className + '__filters__grid__item'}>
                            <InputLabel id='export-filter-assignee-id-list-label'>Assignees</InputLabel>
                            <Select
                                labelId='export-filter-assignee-id-list-label'
                                multiple
                                value={exportFilters.assigneeIdList}
                                label='Assignees'
                                onChange={(e) => handleMultiFilterChange('assigneeIdList', e.target.value)}
                                renderValue={(selected) => {
                                    const selectedAssignees = assigneeOptions.filter((assignee) => selected.includes(assignee.id));
                                    return selectedAssignees.length > 0 ? selectedAssignees.map((assignee) => assignee.label || assignee.userName).join(', ') : 'All';
                                }}>
                                {assigneeOptions.map((assignee) => (
                                    <MenuItem key={assignee.id} value={assignee.id}>
                                        <Checkbox checked={exportFilters.assigneeIdList.includes(assignee.id)} size='small' />
                                        <ListItemText primary={assignee.label || assignee.userName} secondary={assignee.userName} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size='small' className={className + '__filters__grid__item'}>
                            <InputLabel id='export-filter-created-by-email-list-label'>Reporter Emails</InputLabel>
                            <Select
                                labelId='export-filter-created-by-email-list-label'
                                multiple
                                value={exportFilters.createdByEmailList}
                                label='Reporter Emails'
                                onChange={(e) => handleMultiFilterChange('createdByEmailList', e.target.value)}
                                renderValue={(selected) => selected.length > 0 ? selected.join(', ') : 'All'}>
                                {createdByEmailOptions.map((email) => (
                                    <MenuItem key={email} value={email}>
                                        <Checkbox checked={exportFilters.createdByEmailList.includes(email)} size='small' />
                                        <ListItemText primary={email} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box className={className + '__filters__grid__daterange'}>
                            <Typography className={className + '__filters__grid__daterange__label'}>
                                Created Date Range
                            </Typography>
                            <Box className={className + '__filters__grid__daterange__presets'}>
                                {DATE_RANGE_PRESETS.map((preset) => (
                                    <ToggleButton
                                        key={preset.key}
                                        value={preset.key}
                                        size='small'
                                        selected={dateRangePreset === preset.key}
                                        onChange={() => handleDateRangePresetChange(null, preset.key)}
                                        className={className + '__filters__grid__daterange__presets__btn'}
                                    >
                                        {preset.label}
                                    </ToggleButton>
                                ))}
                            </Box>
                            <Box className={className + '__filters__grid__daterange__bottom'}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box className={className + '__filters__grid__daterange__pickers'}>
                                    <DesktopDatePicker
                                        label='From'
                                        format='DD MMM YYYY'
                                        value={exportFilters.createTimeStampFrom ? dayjs(exportFilters.createTimeStampFrom) : null}
                                        maxDate={exportFilters.createTimeStampTo ? dayjs(exportFilters.createTimeStampTo) : undefined}
                                        onChange={(value) => handleDatePickerChange('createTimeStampFrom', value)}
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    />
                                    <span className={className + '__filters__grid__daterange__pickers__sep'}>—</span>
                                    <DesktopDatePicker
                                        label='To'
                                        format='DD MMM YYYY'
                                        value={exportFilters.createTimeStampTo ? dayjs(exportFilters.createTimeStampTo) : null}
                                        minDate={exportFilters.createTimeStampFrom ? dayjs(exportFilters.createTimeStampFrom) : undefined}
                                        onChange={(value) => handleDatePickerChange('createTimeStampTo', value)}
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    />
                                </Box>
                            </LocalizationProvider>
                            <Box className={className + '__filters__grid__daterange__sort'}>
                            <FormControl size='small' className={className + '__filters__grid__daterange__sort__item'}>
                                <InputLabel id='export-filter-sort-by-label'>Sort By</InputLabel>
                                <Select
                                    labelId='export-filter-sort-by-label'
                                    value={exportFilters.sortBy}
                                    label='Sort By'
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}>
                                    <MenuItem value='createTimeStamp'>Reported Date</MenuItem>
                                    <MenuItem value='modifiedTimeStamp'>Last Updated</MenuItem>
                                    <MenuItem value='closeTimeStamp'>Closed Date</MenuItem>
                                    <MenuItem value='ticketId'>Ticket Tag</MenuItem>
                                    <MenuItem value='status'>Status</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size='small' className={className + '__filters__grid__daterange__sort__item'}>
                                <InputLabel id='export-filter-sort-asc-label'>Sort Direction</InputLabel>
                                <Select
                                    labelId='export-filter-sort-asc-label'
                                    value={String(exportFilters.sortAsc)}
                                    label='Sort Direction'
                                    onChange={(e) => handleFilterChange('sortAsc', e.target.value === 'true')}>
                                    <MenuItem value='false'>Descending</MenuItem>
                                    <MenuItem value='true'>Ascending</MenuItem>
                                </Select>
                            </FormControl>
                            </Box>
                        </Box>
                        </Box>
                    </Box>
                </Paper>

                {/* Field Selection Layout */}
                <Box className={className + '__fields-layout'}>
                    {/* Left Side - Available Fields */}
                    <Box className={className + '__fields-layout__available'}>
                        <Typography className={className + '__fields-layout__available__title'}>
                            Available Fields
                        </Typography>
                        
                        {/* Selection Controls */}
                        <Box className={className + '__fields__controls'}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isCheckedSelectAll}
                                        onChange={handleSelectAllField}
                                        className={className + '__fields__controls__selectall'}
                                    />
                                }
                                label={`Select All`}
                                className={className + '__fields__controls__label'}
                            />
                            <Chip
                                label={`${selectedOptions?.length} selected`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        </Box>

                        {/* Available Fields Grid */}
                        <Box className={className + '__fields-grid'}>
                            {/* Standard Fields */}
                            <Paper className={className + '__fields__section'}>
                                <Box className={className + '__fields__section__header'}>
                                    <Typography className={className + '__fields__section__title'}>
                                        Standard Fields
                                    </Typography>
                                    <Chip
                                        label={standardFields.length}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                                
                                <Box className={className + '__fields__section__list'}>
                                    {standardFields.map((option, index) => (
                                        <Box key={option.id} className={className + '__fields__section__list__item'}>
                                            <Box className={className + '__fields__section__list__item__checkbox'}>
                                                <Checkbox
                                                    checked={selectedOptions.findIndex((selectedOption) => selectedOption.field === option.field) > -1}
                                                    onChange={(e) => handleFieldSelect(e, option)}
                                                    size="small"
                                                />
                                            </Box>
                                            <Box className={className + '__fields__section__list__item__content'}>
                                                <TextField
                                                    value={option?.display}
                                                    onChange={(e) => handleOptionDisplayChange(e, downloadOptions.findIndex(item => item.id === option.id))}
                                                    size="small"
                                                    className={className + '__fields__section__list__item__content__field'}
                                                    variant="outlined"
                                                />
                                                <Typography className={className + '__fields__section__list__item__content__description'}>
                                                    {option.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        </Box>
                    </Box>

                    {/* Right Side - Selected Fields Order */}
                    <Box className={className + '__fields-layout__selected'}>
                        <Typography className={className + '__fields-layout__selected__title'}>
                            Export Order ({selectedOptions?.length} fields)
                        </Typography>
                        
                        {selectedOptions.length === 0 ? (
                            <Box className={className + '__fields-layout__selected__empty'}>
                                <Typography className={className + '__fields-layout__selected__empty__text'}>
                                    Select fields from the left panel to set export order
                                </Typography>
                            </Box>
                        ) : (
                            <Paper className={className + '__fields-layout__selected__list'}>
                                {selectedOptions.map((option, index) => (
                                    <Box key={`${option.id}-${index}`} className={`${className}__fields-layout__selected__list__item ${option.customField ? 'custom' : 'standard'}`}>
                                        <Box className={className + '__fields-layout__selected__list__item__drag'}>
                                            <DragIcon />
                                        </Box>
                                        <Box className={className + '__fields-layout__selected__list__item__content'}>
                                            <Box className={className + '__fields-layout__selected__list__item__content__header'}>
                                                <Typography className={className + '__fields-layout__selected__list__item__content__header__title'}>
                                                    {option.display || option.field}
                                                </Typography>
                                                <Chip
                                                    label={option.customField ? 'Custom' : 'Standard'}
                                                    size="small"
                                                    color={option.customField ? 'warning' : 'primary'}
                                                    variant="outlined"
                                                />
                                            </Box>
                                            <Typography className={className + '__fields-layout__selected__list__item__content__description'}>
                                                {option.description}
                                            </Typography>
                                        </Box>
                                        <Box className={className + '__fields-layout__selected__list__item__actions'}>
                                            <IconButton
                                                onClick={() => moveSelectedField(index, 'up')}
                                                disabled={index === 0}
                                                size="small"
                                                className={className + '__fields-layout__selected__list__item__actions__up'}
                                            >
                                                <ArrowUpIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => moveSelectedField(index, 'down')}
                                                disabled={index === selectedOptions.length - 1}
                                                size="small"
                                                className={className + '__fields-layout__selected__list__item__actions__down'}
                                            >
                                                <ArrowDownIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        )}
                    </Box>
                </Box>

                {selectedOptions.length === 0 && (
                    <Alert severity="info" className={className + '__alert'} icon={<InfoIcon />}>
                        <Typography>
                            Please select at least one field to export.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions className={className + '__actions'}>
                <Button
                    onClick={props.closePopup}
                    disabled={cursor === 'wait'}
                    variant="outlined"
                    className={className + '__actions__cancel'}
                >
                    Cancel
                </Button>
                <Button
                    onClick={updateSetDefaultDownloadOptionsPreference}
                    disabled={cursor === 'wait' || selectedOptions?.length === 0}
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    className={className + '__actions__default'}
                >
                    Set as Default
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={cursor === 'wait' || selectedOptions?.length === 0}
                    variant="contained"
                    startIcon={cursor === 'wait' ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    className={className + '__actions__download'}
                >
                    {cursor === 'wait' ? 'Processing...' : `Download ${exportToExcel ? 'Excel' : 'CSV'}`}
                </Button>
            </DialogActions>

            {/* Variable Selection Menu */}
            <Menu
                anchorEl={variableMenuAnchor}
                open={Boolean(variableMenuAnchor)}
                onClose={handleVariableMenuClose}
                className={className + '__variable-menu'}
                PaperProps={{
                    className: className + '__variable-menu__paper'
                }}
            >
                <Box className={className + '__variable-menu__header'}>
                    <Typography className={className + '__variable-menu__header__title'}>
                        Insert Variable
                    </Typography>
                </Box>
                <Divider />
                
                {/* Standard Variables */}
                {getAvailableVariables().filter(v => v.type === 'standard').length > 0 && (
                    <>
                        <Box className={className + '__variable-menu__section-header'}>
                            <Typography className={className + '__variable-menu__section-header__title'}>
                                Standard Fields
                            </Typography>
                        </Box>
                        {getAvailableVariables()
                            .filter(variable => variable.type === 'standard')
                            .map((variable) => (
                                <MenuItem
                                    key={variable.name}
                                    onClick={() => insertVariable(variable.name)}
                                    className={className + '__variable-menu__item'}
                                >
                                    <ListItemIcon>
                                        <VariableIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`{${variable.name}}`}
                                        secondary={variable.description}
                                    />
                                </MenuItem>
                            ))
                        }
                    </>
                )}
                
                {/* Custom Field Variables */}
                {getAvailableVariables().filter(v => v.type === 'custom').length > 0 && (
                    <>
                        <Divider />
                        <Box className={className + '__variable-menu__section-header'}>
                            <Typography className={className + '__variable-menu__section-header__title'}>
                                Custom Fields
                            </Typography>
                        </Box>
                        {getAvailableVariables()
                            .filter(variable => variable.type === 'custom')
                            .map((variable) => (
                                <MenuItem
                                    key={variable.name}
                                    onClick={() => insertVariable(variable.name)}
                                    className={`${className}__variable-menu__item custom`}
                                >
                                    <ListItemIcon>
                                        <VariableIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`{${variable.name}}`}
                                        secondary={variable.description}
                                    />
                                </MenuItem>
                            ))
                        }
                    </>
                )}
                
                {getAvailableVariables().length === 0 && (
                    <Box className={className + '__variable-menu__empty'}>
                        <Typography className={className + '__variable-menu__empty__text'}>
                            No variables available
                        </Typography>
                    </Box>
                )}
            </Menu>

            {/* Export History Drawer */}
            <Drawer
                size={'lg'}
                anchor='right'
                onClose={handleDrawer}
                open={openDrawer}
                className={className + '__body'}>
                <div className={className + '__body__inner'}>
                    <div className={className + '__body__inner__top'}>
                        <div className={className + '__body__inner__top__left'}>
                            <div className={className + '__body__inner__top__left__title'}>
                                Ticket Export History
                            </div>
                            <div className={className + '__body__inner__top__left__subtitle'}>
                                {ticketExportJobList.length} export {ticketExportJobList.length === 1 ? 'job' : 'jobs'}
                            </div>
                        </div>
                        <IconButton
                            size='small'
                            className={className + '__body__inner__top__close'}
                            onClick={handleDrawer}
                            aria-label='Close history panel'>
                            <Close />
                        </IconButton>
                    </div>
                    <div className={className + '__body__inner__bottom'}>
                        {
                            isLoading ?
                                <Box className={className + '__body__inner__bottom__loading'}>
                                    <CircularProgress />
                                    <Typography className={className + '__body__inner__bottom__loading__text'}>
                                        Loading export history...
                                    </Typography>
                                </Box>
                            :
                                <div className={className + '__body__inner__bottom__item'}>
                                    {
                                        ticketExportJobList.length === 0 ?
                                            <div className={className + '__body__inner__bottom__item__noItem'}>
                                                <HistoryIcon className={className + '__body__inner__bottom__item__noItem__icon'} />
                                                <div className={className + '__body__inner__bottom__item__noItem__title'}>
                                                    No exports yet
                                                </div>
                                                <div className={className + '__body__inner__bottom__item__noItem__subtitle'}>
                                                    Start a download and your export jobs will appear here.
                                                </div>
                                            </div>
                                        :
                                            ticketExportJobList.map((item, index)=>
                                                <TicketExportJobRow
                                                    key={item.id}
                                                    item={item}
                                                    index={index + 1} />
                                            )
                                    }
                                </div>
                        }
                    </div>
                </div>
            </Drawer>

            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                open={snackBarOpen}
                autoHideDuration={5000}
                onClose={handleSnackBarClose}
            >
                <Alert
                    onClose={handleSnackBarClose}
                    severity={snackBarType}
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {snackBarMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}
